// R119 (Jesse: "I feel like this should be a lot closer. The gross formula
// builder is lacking I think.") — the client mirror of the VR Platform
// statement ladder (contract 2.1): lib/ownerFinance COLUMN_LABELS (key order IS
// the ladder order), FORMULA_BOUNDS and the preset ids. A statement formula is
// that ladder with a definition per column; the builder (pages/statementBuilder),
// the Statement formulas table and Property Settings all read it from here.
// scripts/test_statement_builder_dom.mjs pins the labels and bounds to the
// server's, so a drift fails a test instead of a save.
// R121 (Jesse: "I am also confused on where to build the Gross Revenue formula" /
// "What is the difference between the statement and commission P&L formulas?"):
// the explainers every page shows (EXPLAIN) live here, once.

export type LadderKey = 'rents' | 'resolutions' | 'cleaning_col' | 'gross' | 'channel_fee' | 'net_revenue' | 'noncomm' | 'total' | 'commission' | 'expenses' | 'net_income';
export interface LadderRow { key: LadderKey; label: string; sign: 1 | -1 | 0; computed: string | null }

// sign: 1 adds to the owner's money, -1 takes from it, 0 = a computed subtotal
export const LADDER: readonly LadderRow[] = Object.freeze([
  { key: 'rents', label: 'Rents', sign: 1, computed: null },
  { key: 'resolutions', label: 'Resolutions/Fees', sign: 1, computed: null },
  { key: 'cleaning_col', label: 'Cleaning Fees', sign: 1, computed: null },
  { key: 'gross', label: 'Gross Revenue', sign: 0, computed: 'Rents + Resolutions/Fees + Cleaning Fees' },
  { key: 'channel_fee', label: 'Channel Fees', sign: -1, computed: null },
  { key: 'net_revenue', label: 'Net Revenue', sign: 0, computed: 'Gross Revenue − Channel Fees' },
  { key: 'noncomm', label: 'Non-Commissionable Fees', sign: 1, computed: null },
  { key: 'total', label: 'Total', sign: 0, computed: 'Net Revenue + Non-Commissionable Fees' },
  { key: 'commission', label: 'Management Commission', sign: -1, computed: null },
  { key: 'expenses', label: 'Expenses', sign: -1, computed: null },
  { key: 'net_income', label: 'Net Income', sign: 0, computed: 'Total − Management Commission − Expenses' },
] as LadderRow[]);
export const LADDER_KEYS: readonly LadderKey[] = LADDER.map(r => r.key);
export const LADDER_LABEL: Readonly<Record<string, string>> = Object.freeze(Object.fromEntries(LADDER.map(r => [r.key, r.label])));

// Per booking (contract 2.1 STATEMENT_COLUMNS): the ladder up to commission,
// then owner_net = Total − Management Commission (expenses are monthly).
export const BOOKING_KEYS = ['rents', 'resolutions', 'cleaning_col', 'gross', 'channel_fee', 'net_revenue', 'noncomm', 'total', 'commission', 'owner_net'] as const;
export type BookingKey = typeof BOOKING_KEYS[number];
export const BOOKING_LABEL: Readonly<Record<BookingKey, string>> = Object.freeze({
  rents: 'Rents', resolutions: 'Resolutions', cleaning_col: 'Cleaning', gross: 'Gross', channel_fee: 'Channel fee',
  net_revenue: 'Net revenue', noncomm: 'Non-comm', total: 'Total', commission: 'Commission', owner_net: 'Total − commission',
});
export const BOOKING_DEFAULT_COLS: readonly BookingKey[] = ['rents', 'cleaning_col', 'gross', 'channel_fee', 'noncomm', 'commission', 'owner_net'];

// lib/ownerFinance FORMULA_BOUNDS — the limits formulaErrors / profileErrors enforce
// R121: − cleaning_cost (no cleaning estimate); + company_fee and schedule_max (the company fee by booking date)
export const FORMULA_BOUNDS = Object.freeze({
  name_max: 120, description_max: 300,
  commission_rate: [0, 60] as readonly [number, number],
  min_fee: [0, 5000] as readonly [number, number],
  channel_rate: [0, 50] as readonly [number, number],
  company_fee: [0, 5000] as readonly [number, number],
  schedule_max: 12,
  max_formulas: 100,
});

export const PRESET_IDS = ['auto', 'A', 'B', 'C', 'D', 'E', 'F', 'custom'] as const;
export type PresetId = typeof PRESET_IDS[number];
export const RATE_CHANNELS: ReadonlyArray<[string, string]> = [['airbnb', 'Airbnb'], ['vrbo', 'Vrbo'], ['bookingcom', 'Booking.com'], ['direct', 'Direct']];
export const RATE_CHIPS = [5, 10, 14, 14.5, 15, 18, 20, 22, 25];

// A statement formula's column definitions (data model "columns").
export interface Columns {
  cancellations: 'airbnb_retained' | 'none';
  owner_fees: 'resolutions' | 'noncomm' | 'company';
  cleaning: 'auto' | 'gross' | 'noncomm' | 'company' | 'auto_not_gross';   // auto_not_gross: legacy rules only, never offered
  fee_on_cleaning: 'auto' | boolean;
  fee_on_owner_fees: boolean;
  channel_rates: null | Record<string, number | null>;
  commission_base: 'auto' | 'gross' | 'net';
  commission_rate: number | null;
  min_fee: number | null;
}
// the built-in "Automatic" shape (preset auto = statement_default)
export const COLUMN_DEFAULTS: Readonly<Columns> = Object.freeze({
  cancellations: 'airbnb_retained', owner_fees: 'resolutions', cleaning: 'auto', fee_on_cleaning: 'auto', fee_on_owner_fees: false,
  channel_rates: null, commission_base: 'auto', commission_rate: null, min_fee: null,
});
// the keys a preset (and matchFormula) compares — lib/ownerFinance SHAPE_KEYS: the rate and the
// minimum are the property's; R119 review #15: channel_rates IS shape (preset E · Lease names zero
// rates; every other preset none), compared by value — the server's presetOf, so the builder never
// names a preset the server stores as Custom
// R121: − cleaning_expense (the R119 cleaning estimate is retired — lib/ownerFinance SHAPE_KEYS)
export const SHAPE_KEYS: ReadonlyArray<keyof Columns> = ['cancellations', 'owner_fees', 'cleaning', 'fee_on_cleaning', 'fee_on_owner_fees', 'channel_rates', 'commission_base'];

export interface Preset { id: string; label: string; columns: Partial<Columns> }
// Fallback when GET /api/owner-finance/rules sends no `presets` (the server's win).
export const PRESETS_FALLBACK: readonly Preset[] = Object.freeze([
  { id: 'auto', label: 'Automatic — follows each property\'s commission formula', columns: {} },
  { id: 'A', label: 'Cleaning in Gross · fee on rents + cleaning · % of net revenue', columns: { cleaning: 'gross', fee_on_cleaning: true, commission_base: 'net' } },
  { id: 'B', label: 'No cleaning · fee on rents · % of gross', columns: { cleaning: 'company', fee_on_cleaning: false, commission_base: 'gross' } },
  { id: 'C', label: 'No cleaning · fee on rents · % of net revenue', columns: { cleaning: 'company', fee_on_cleaning: false, commission_base: 'net' } },
  { id: 'D', label: 'Cleaning paid to owner, Non-Commissionable · fee on rents + cleaning · % of net revenue', columns: { cleaning: 'noncomm', fee_on_cleaning: true, commission_base: 'net' } },
  { id: 'E', label: 'Lease · % of gross, no cancellations, no channel fee', columns: { cleaning: 'company', fee_on_cleaning: false, commission_base: 'gross', cancellations: 'none', channel_rates: { airbnb: 0, vrbo: 0, bookingcom: 0, direct: 0 } } },
  { id: 'F', label: 'Cleaning + owner fees in Gross · fee on gross · % of net revenue', columns: { cleaning: 'gross', fee_on_cleaning: true, fee_on_owner_fees: true, commission_base: 'net' } },
] as Preset[]);

const pick = <T extends string>(v: unknown, ok: readonly T[], d: T): T => (ok.includes(v as T) ? (v as T) : d);
const numOrNull = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** Fill / coerce a columns object (unknown → the Automatic default). */
export function normColumns(c: any): Columns {
  const s = c && typeof c === 'object' ? c : {};
  const cr = s.channel_rates && typeof s.channel_rates === 'object' ? s.channel_rates : null;
  const rates = cr ? Object.fromEntries(RATE_CHANNELS.map(([k]) => [k, numOrNull(cr[k])])) : null;
  return {
    cancellations: pick(s.cancellations, ['airbnb_retained', 'none'], COLUMN_DEFAULTS.cancellations),
    owner_fees: pick(s.owner_fees, ['resolutions', 'noncomm', 'company'], COLUMN_DEFAULTS.owner_fees),
    cleaning: pick(s.cleaning, ['auto', 'gross', 'noncomm', 'company', 'auto_not_gross'], COLUMN_DEFAULTS.cleaning),
    fee_on_cleaning: s.fee_on_cleaning === true || s.fee_on_cleaning === false ? s.fee_on_cleaning : 'auto',
    fee_on_owner_fees: s.fee_on_owner_fees === true,
    channel_rates: rates && Object.values(rates).some(v => v != null) ? rates : null,
    commission_base: pick(s.commission_base, ['auto', 'gross', 'net'], COLUMN_DEFAULTS.commission_base),
    commission_rate: numOrNull(s.commission_rate),
    min_fee: numOrNull(s.min_fee),
  };
}

/** Design "LEGACY DERIVATION": an R118 gross rule (include / channel_fees) → columns. */
export function legacyColumns(rule: any): Columns {
  const inc = (rule && rule.include) || {};
  return normColumns({
    cleaning: inc.cleaning === 'always' ? 'gross' : inc.cleaning === 'never' ? 'auto_not_gross' : 'auto',
    owner_fees: inc.owner_fees === false ? 'noncomm' : 'resolutions',
    fee_on_cleaning: 'auto', fee_on_owner_fees: false, commission_base: 'auto', commission_rate: null, min_fee: null,
    cancellations: 'airbnb_retained',
  });
}

/** A stored formula's columns: the server's (schema 2 / normalized) else the legacy derivation. */
export function formulaColumns(f: any): Columns {
  if (f && f.columns && typeof f.columns === 'object' && (f.schema === 2 || f.legacy != null)) return normColumns(f.columns);
  return legacyColumns(f);
}
/** Made before R119 (no schema 2) — the built-in default is the Automatic preset, never "legacy". */
export function isLegacy(f: any): boolean {
  if (!f) return false;
  if (f.legacy != null) return !!f.legacy;
  return f.schema !== 2 && f.id !== 'statement_default';
}

/** Two channel-rate sets are the same (null = none named; a channel left out = null). */
export function sameRates(a: Columns['channel_rates'], b: Columns['channel_rates']): boolean {
  if (a == null || b == null) return a == null && b == null;
  return RATE_CHANNELS.every(([k]) => numOrNull(a[k]) === numOrNull(b[k]));
}
const sameCol = (k: keyof Columns, a: Columns, b: Columns): boolean => (k === 'channel_rates' ? sameRates(a.channel_rates, b.channel_rates) : a[k] === b[k]);
/** A preset's full columns (the server sends them full; the fallback is sparse). */
export const presetColumns = (p: Preset): Columns => normColumns({ ...COLUMN_DEFAULTS, ...((p && p.columns) || {}) });
const listed = (presets: readonly Preset[]) => presets.filter(p => p && (PRESET_IDS as readonly string[]).includes(p.id) && p.id !== 'custom');
/** The preset whose shape equals these columns, else 'custom' — lib/ownerFinance presetOf
 *  (R119 review #15: channel rates included, by value). */
export function presetOf(c: Columns, presets: readonly Preset[] = PRESETS_FALLBACK): PresetId {
  for (const p of listed(presets)) {
    const full = presetColumns(p);
    if (SHAPE_KEYS.every(k => sameCol(k, full, c))) return p.id as PresetId;
  }
  return 'custom';
}
/** The preset these columns would be with no channel rates of their own (a Custom shape that
 *  is "D + its own rates"), else null — the builder says so instead of a bare "Custom". */
export function presetBesideRates(c: Columns, presets: readonly Preset[] = PRESETS_FALLBACK): PresetId | null {
  if (presetOf(c, presets) !== 'custom' || c.channel_rates == null) return null;
  for (const p of listed(presets)) {
    const full = presetColumns(p);
    if (full.channel_rates == null && SHAPE_KEYS.every(k => k === 'channel_rates' || sameCol(k, full, c))) return p.id as PresetId;
  }
  return null;
}
/** The columns a preset chip sets; the rate / minimum stay. R119 review #15: a preset that NAMES
 *  channel rates (E · Lease: none) sets them; every other one leaves the rates the office typed —
 *  except rates that are another preset's own (E's zeros), which leave with that preset. */
export function applyPreset(c: Columns, p: Preset, presets: readonly Preset[] = PRESETS_FALLBACK): Columns {
  const full = presetColumns(p);
  const out: Columns = { ...c };
  for (const k of SHAPE_KEYS) if (k !== 'channel_rates') (out as any)[k] = full[k];
  if (full.channel_rates != null) out.channel_rates = { ...full.channel_rates };
  else if (c.channel_rates != null && listed([...presets, p]).some(q => { const r = presetColumns(q).channel_rates; return r != null && sameRates(r, c.channel_rates); })) out.channel_rates = null;
  return out;
}
export const presetName = (id: string): string => (id === 'auto' ? 'Automatic' : id === 'custom' ? 'Custom' : id);

const pctTxt = (n: number) => `${Math.round(n * 100) / 100}%`;
/** The statement shape in words — the Statement formulas table chips. */
export function shapeChips(c: Columns): string[] {
  const out: string[] = [];
  out.push(c.cleaning === 'gross' ? 'Cleaning → Gross' : c.cleaning === 'noncomm' ? 'Cleaning → paid to owner' : c.cleaning === 'company' ? 'Cleaning → company'
    : c.cleaning === 'auto_not_gross' ? 'Cleaning → automatic, never Gross' : 'Cleaning → automatic');
  const fee = c.fee_on_cleaning === true ? 'rents + cleaning' : c.fee_on_cleaning === false ? 'rents' : 'rents (+ cleaning when the owner\'s)';
  out.push(`Fee on ${fee}${c.fee_on_owner_fees ? ' + owner fees' : ''}`);
  const base = c.commission_base === 'gross' ? 'of gross' : c.commission_base === 'net' ? 'of net revenue' : '(base from the commission formula)';
  out.push(`${c.commission_rate != null ? pctTxt(c.commission_rate) : 'property\'s %'} ${base}`);
  if (c.min_fee != null) out.push(`min $${c.min_fee} per booking`);
  if (c.owner_fees === 'noncomm') out.push('Owner fees → paid to owner');
  else if (c.owner_fees === 'company') out.push('Owner fees → company');
  if (c.cancellations === 'airbnb_retained') out.push('+ cancellations');
  return out;
}

// ── preview statuses (contract 2.7: ok ≤ $1, close ≤ max($5, 1% of the statement), else off) ──
export type Status = 'ok' | 'close' | 'off';
export interface Thresholds { ok_abs: number; close_abs: number; close_pct: number }
export const DEFAULT_THRESHOLDS: Readonly<Thresholds> = Object.freeze({ ok_abs: 1, close_abs: 5, close_pct: 1 });
/** The server sends each status; this is the same rule for a cell it left out. */
export function statusOf(delta: number, stmt: number, th: Thresholds = DEFAULT_THRESHOLDS): Status {
  const d = Math.abs(Number(delta) || 0);
  if (d <= th.ok_abs + 1e-9) return 'ok';
  if (d <= Math.max(th.close_abs, Math.abs(Number(stmt) || 0) * th.close_pct / 100) + 1e-9) return 'close';
  return 'off';
}
export const STATUS_LABEL: Readonly<Record<Status, string>> = Object.freeze({ ok: 'matches', close: 'close', off: 'off' });
export const isStatus = (s: unknown): s is Status => s === 'ok' || s === 'close' || s === 'off';

export function fmtMoney(v: unknown): string {
  if (v == null || v === '' || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** "+$6.58" / "−$102.53" / "$0.00" */
export function fmtDelta(v: unknown): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v) * 100) / 100;
  if (n === 0) return '$0.00';
  return `${n > 0 ? '+' : '−'}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** A Δ in words for a tinted cell: "✓ $0.00" / "≈ +$3.20" / "✗ −$120.00". */
export function fmtStatus(s: Status, delta: unknown): string {
  return `${s === 'ok' ? '✓' : s === 'close' ? '≈' : '✗'} ${fmtDelta(delta)}`;
}

// where an effective setting came from (contract 2.3 `from`)
export const FROM_LABEL: Readonly<Record<string, string>> = Object.freeze({
  profile: 'property facts', formula: 'this formula', revenue_formula: 'commission formula', owner_override: 'owner\'s formula override',
  internal_expenses: 'Internal Expenses', template: 'template', store: 'shared rate', default: 'default',
});

/** 'YYYY-MM' → "Aug 2026" */
export function monthLabel(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return String(ym || '');
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)).toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}
/** The calendar month n months from this one ('YYYY-MM'; n < 0 = back). */
export function monthFromNow(n: number, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// R121: the explainers — one wording everywhere (the builder, Formulas & Fees, Property Settings)
export const EXPLAIN = Object.freeze({
  gross_where: 'Gross Revenue is built here: it is one column of the statement formula. Gross Revenue = Rents + Resolutions/Fees + Cleaning Fees (when cleaning is In Gross).',
  statement: 'Statement formula: copies how VR Platform lays out the owner\'s statement (what is in Gross, what the channel fee is taken on, what the commission is taken on). It is what owners see on the portal.',
  commission: 'Commission (P&L) formula: how the company books each reservation in its own P&L (management rate, what is commissionable, whose cleaning it is).',
  automatic: 'The built-in Automatic statement formula borrows cleaning placement, the fee base and the commission base from the Commission (P&L) formula. That borrowing can disagree with the statements: Learn from the statements to fix it.',
  no_guess: 'Expenses come from the statement. Until it is out: only the owner-billed work orders, maintenance and supplies logged in Dash. Nothing is estimated.',
});
/** R121: a company fee schedule in words — "$77 before Apr 1, 2026, then $100" (by booking date) */
export function scheduleText(s: unknown): string {
  if (!Array.isArray(s) || s.length < 2) return '';
  const steps = s as Array<{ since: string | null; fee: number; by?: string }>;
  const d = (x: string) => new Date(`${x}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const by = steps[0] && steps[0].by === 'stay' ? 'stays starting' : 'bookings made';
  const parts = steps.map((e, i) => (i === 0 ? `$${Number(e.fee)} for ${by} before ${d(String(steps[1].since))}` : `$${Number(e.fee)} from ${d(String(e.since))}`));
  return parts.join(', then ');
}
